import { json2csv } from "json-2-csv";

import { saveBlob } from "./save-file";

export const EXCEL_CSV_DELIMITER = {
  field: ";" as const,
  wrap: '"' as const,
  eol: "\n" as const,
};

export const downloadCsvURI = (content: string, name: string): void => {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  void saveBlob(blob, name, "text/csv;charset=utf-8;");
};

export interface ICsvHeader {
  field: string;
  title: string;
}

export interface IDownloadCsvOptions {
  excelForceTextCells?: boolean;
}

export interface ICsvPreambleRow {
  label: string;
  value?: unknown;
}

const excelTextCell = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "";
  }
  return `\t${String(value)}`;
};

const mapRowsForExcel = (content: Record<string, any>[]): Record<string, string>[] =>
  content.map(row => {
    const out: Record<string, string> = {};
    for (const key of Object.keys(row)) {
      out[key] = excelTextCell(row[key]);
    }
    return out;
  });

const escapeCsvCell = (value: unknown): string => {
  const stringValue = value === null || value === undefined ? "" : String(value);
  const escaped = stringValue.replace(/"/g, '""');
  return `${EXCEL_CSV_DELIMITER.wrap}${escaped}${EXCEL_CSV_DELIMITER.wrap}`;
};

export const stringifyCsv = (
  content: Record<string, any>[],
  keys: ICsvHeader[],
  options?: IDownloadCsvOptions & { includeBom?: boolean },
): string => {
  const { excelForceTextCells = true, includeBom = true } = options ?? {};
  const rows = excelForceTextCells ? mapRowsForExcel(content) : content;

  return json2csv(rows, {
    excelBOM: includeBom,
    keys,
    delimiter: EXCEL_CSV_DELIMITER,
  });
};

export const downloadCsv = (
  content: Record<string, any>[],
  fileName: string,
  keys: ICsvHeader[],
  options?: IDownloadCsvOptions,
) => {
  const csv = stringifyCsv(content, keys, options);
  downloadCsvURI(csv, fileName);
};

export const downloadCsvWithPreamble = (
  preambleRows: ICsvPreambleRow[],
  content: Record<string, any>[],
  fileName: string,
  keys: ICsvHeader[],
  options?: IDownloadCsvOptions,
) => {
  const preamble = preambleRows
    .map(({ label, value }) =>
      [escapeCsvCell(label), escapeCsvCell(options?.excelForceTextCells === false ? value : excelTextCell(value))].join(
        EXCEL_CSV_DELIMITER.field,
      ),
    )
    .join(EXCEL_CSV_DELIMITER.eol);
  const csv = stringifyCsv(content, keys, { ...options, includeBom: false });
  const fullContent = `\uFEFF${preamble}${EXCEL_CSV_DELIMITER.eol}${EXCEL_CSV_DELIMITER.eol}${csv}`;

  downloadCsvURI(fullContent, fileName);
};
